import { useReducer, useCallback, useEffect } from 'react';
import { fetchReactionStats } from '@/api/adminApis/adminApis';
import { REACTION_GROUP_BY } from '@/constants/reactionStats';
import Toast from '@/utils/toastManager';

const INITIAL_STATE = {
	selectionOptions: [],
	isOptionsLoading: false,
	primarySelection: null,
	secondarySelection: null,
	primaryStats: [],
	secondaryStats: [],
	isPrimaryStatsLoading: false,
	isSecondaryStatsLoading: false
};

const ACTION_TYPES = {
	SET_OPTIONS_LOADING: 'setOptionsLoading',
	SET_OPTIONS: 'setOptions',
	SET_SELECTED_ITEM_1: 'setSelectedItem1',
	SET_SELECTED_ITEM_2: 'setSelectedItem2',
	SET_STATS_LOADING_1: 'setStatsLoading1',
	SET_STATS_LOADING_2: 'setStatsLoading2',
	SET_STATS_1: 'setStats1',
	SET_STATS_2: 'setStats2',
	RESET: 'reset'
};

function reducer(state, action) {
	switch (action.type) {
		case ACTION_TYPES.SET_OPTIONS_LOADING:
			return { ...state, isOptionsLoading: action.payload };
		case ACTION_TYPES.SET_OPTIONS:
			return { ...state, selectionOptions: action.payload };
		case ACTION_TYPES.SET_SELECTED_ITEM_1:
			return { ...state, primarySelection: action.payload, primaryStats: [] };
		case ACTION_TYPES.SET_SELECTED_ITEM_2:
			return { ...state, secondarySelection: action.payload, secondaryStats: [] };
		case ACTION_TYPES.SET_STATS_LOADING_1:
			return { ...state, isPrimaryStatsLoading: action.payload };
		case ACTION_TYPES.SET_STATS_LOADING_2:
			return { ...state, isSecondaryStatsLoading: action.payload };
		case ACTION_TYPES.SET_STATS_1:
			return { ...state, primaryStats: action.payload };
		case ACTION_TYPES.SET_STATS_2:
			return { ...state, secondaryStats: action.payload };
		case ACTION_TYPES.RESET:
			return { ...INITIAL_STATE, selectionOptions: state.selectionOptions };
		default:
			return state;
	}
}

export function useCompare(isOpen, groupBy) {
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
	const isCategoryGrouping = groupBy === REACTION_GROUP_BY.CATEGORY;

	const loadSelectionOptions = useCallback(async () => {
		if (!groupBy) return;
		dispatch({ type: ACTION_TYPES.SET_OPTIONS_LOADING, payload: true });
		try {
			const { data } = await fetchReactionStats({ groupBy });
			const filteredData = data.filter(item => item.categoryName || item.categoryId || item.instructorName);
			const formattedOptions = filteredData
				.filter(item => item.categoryId || item.instructorName)
				.map(item => ({
					value: item.categoryId || item.instructorName,
					label: item.categoryName || item.instructorName
				}));

			dispatch({ type: ACTION_TYPES.SET_OPTIONS, payload: formattedOptions });
		} catch {
			Toast.error(`Error fetching stats for ${selectedItem.label}`);
		} finally {
			dispatch({ type: ACTION_TYPES.SET_OPTIONS_LOADING, payload: false });
		}
	}, [groupBy]);

	const loadStatsForSelection = useCallback(
		async (selectedItem, target) => {
			if (!selectedItem) return;
			const loadingType = target === 1 ? ACTION_TYPES.SET_STATS_LOADING_1 : ACTION_TYPES.SET_STATS_LOADING_2;
			const statsType = target === 1 ? ACTION_TYPES.SET_STATS_1 : ACTION_TYPES.SET_STATS_2;

			dispatch({ type: loadingType, payload: true });
			try {
				const { data } = await fetchReactionStats({
					groupBy,
					data: selectedItem.value instanceof Array ? selectedItem.value[0] : selectedItem.value
				});
				dispatch({ type: statsType, payload: data || [] });
			} catch {
				Toast.error(`Error fetching stats for ${selectedItem.label}`);
				dispatch({ type: statsType, payload: [] });
			} finally {
				dispatch({ type: loadingType, payload: false });
			}
		},
		[groupBy]
	);

	useEffect(() => {
		if (isOpen) loadSelectionOptions();
		else dispatch({ type: ACTION_TYPES.RESET });
	}, [isOpen, loadSelectionOptions]);

	const handlePrimarySelect = item => {
		dispatch({ type: ACTION_TYPES.SET_SELECTED_ITEM_1, payload: item });
		if (item) loadStatsForSelection(item, 1);
	};

	const handleSecondarySelect = item => {
		dispatch({ type: ACTION_TYPES.SET_SELECTED_ITEM_2, payload: item });
		if (item) loadStatsForSelection(item, 2);
	};

	return {
		...state,
		isCategoryGrouping,
		loadStatsForSelection,
		loadSelectionOptions,
		dispatch,
		handlePrimarySelect,
		handleSecondarySelect
	};
}
